import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SeoHelmet from "@/components/SeoHelmet";
import { ArrowLeft, ArrowRight, Calendar, Clock, User, Twitter, Linkedin, Link as LinkIcon, Chrome, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface BlogPostData {
  title: string;
  slug: string;
  date: string;
  author: string;
  readingTime: string;
  image: string;
  category: string;
  description: string;
  contentBeforeCTA: string;
  contentAfterCTA: string;
}

const blogPosts: Record<string, BlogPostData> = {
  "how-to-tailor-your-cv": {
    title: "How to Tailor Your CV for a Specific Job (2026 Guide)",
    slug: "how-to-tailor-your-cv",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "6 min read",
    image: "/blog-tailoring.jpg",
    category: "Career Advice",
    description: "Discover the step-by-step guide to tailoring your CV for any job description to beat the ATS and land more interviews.",
    contentBeforeCTA: `
<p class="lead">You have applied to 50 jobs. You have rewritten your CV ten times. You are still getting ignored.</p>
<p>The problem is not you. The problem is sending the same CV to every job.</p>
<p>Recruiters use Applicant Tracking Systems (ATS) to scan CVs for keywords. If your CV does not match the job description, it gets rejected before a human reads it.</p>
<p>This guide will show you exactly how to tailor your CV for any job. You will learn the manual process and how to automate it with AI.</p>
<h2>What Is CV Tailoring?</h2>
<p>CV tailoring means adjusting your resume for a specific job application. You are not changing your experience. You are reframing it to match what the employer wants.</p>
<p>A tailored CV:</p>
<ul>
  <li>Uses keywords from the job description</li>
  <li>Highlights relevant achievements</li>
  <li>Removes irrelevant skills</li>
  <li>Reorders bullet points by importance</li>
</ul>
<p>A generic CV does none of this. That is why it fails.</p>
<h2>Why Tailoring Matters for Job Seekers</h2>
<p>According to a recent study, 75% of large companies use ATS software. These systems scan your CV before a recruiter sees it. They look for specific keywords, phrases, and formatting.</p>
<p>If your CV is generic, the ATS will score it low. You will never get a call back.</p>
<p>Tailoring your CV increases your ATS score. It also makes your application stand out to recruiters. When they see your CV matches their requirements exactly, they are more likely to invite you for an interview.</p>
<h2>How to Tailor Your CV Manually (Step by Step)</h2>
<p>You can do this yourself. It takes 10-15 minutes per job.</p>
<h3>Step 1: Copy the job description</h3>
<p>Paste the entire job description into a document or a keyword extractor tool.</p>
<h3>Step 2: Identify key keywords</h3>
<p>Look for skills mentioned more than once, required qualifications, and tools/software like Salesforce or Python.</p>
<h3>Step 3: Open your master CV</h3>
<p>Keep your original CV as a template. Do not edit it directly. Make a copy for each job.</p>
<h3>Step 4: Match keywords to your experience</h3>
<p>Go through each bullet point. Rewrite them to include the keywords you identified.</p>
<div class="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-xl my-8 font-body">
  <p class="font-bold mb-2">Example:</p>
  <p class="text-slate-500 mb-2 font-mono text-sm italic">Before: "Managed social media campaigns"</p>
  <p class="text-cyan-600 dark:text-cyan-400 font-mono text-sm font-bold">After: "Managed paid social media campaigns across Meta Ads, achieving 30% ROI increase"</p>
</div>
<h3>Step 5: Remove irrelevant content</h3>
<p>Delete any bullet points that do not relate to the job. If a skill is not mentioned, consider removing it.</p>
    `,
    contentAfterCTA: `
<h2>FAQ</h2>
<div class="space-y-6 mb-12">
  <div>
    <h4 class="font-bold text-lg mb-2">How long does manual CV tailoring take?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">10-15 minutes per job for an experienced job seeker.</p>
  </div>
  <div>
    <h4 class="font-bold text-lg mb-2">Can I use the same tailored CV for multiple jobs?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">No. Each job has a different description. You must tailor for each role individually.</p>
  </div>
</div>
<h2>Conclusion</h2>
<p>Tailoring your CV for each job is the single most effective way to get more interviews. You can do it manually, or you can use Alvaru to do it in seconds.</p>
    `
  },
  "best-ats-friendly-cv-templates": {
    title: "Best ATS-Friendly CV Templates for 2026",
    slug: "best-ats-friendly-cv-templates",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "5 min read",
    image: "/blog-ats-templates.jpg",
    category: "CV Templates",
    description: "Learn which CV formats and templates are most effective at passing Applicant Tracking Systems (ATS) in 2026.",
    contentBeforeCTA: `
<p class="lead">You spend hours perfecting your CV. You hit submit. You never hear back.</p>
<p>Chances are, an Applicant Tracking System (ATS) rejected your CV before a human ever saw it. No matter how qualified you are.</p>
<p>This guide covers the best ATS-friendly CV formats for 2026. You will learn what works, what does not, and how Alvaru automates the process.</p>

<h2>What Makes a CV ATS-Friendly?</h2>
<p>An ATS-friendly CV follows strict formatting rules. These systems cannot read complicated layouts, graphics, or unusual fonts.</p>
<p>Here are the non-negotiable rules:</p>
<ul>
  <li><strong>Use a single-column layout</strong> – ATS software reads top to bottom, left to right.</li>
  <li><strong>Stick to standard fonts</strong> – Use Arial, Calibri, Helvetica, or Times New Roman.</li>
  <li><strong>Avoid headers and footers</strong> – Many ATS systems do not read text in these areas.</li>
  <li><strong>Do not use tables or text boxes</strong> – These elements break the parsing logic.</li>
  <li><strong>Save as PDF or Word</strong> – Both formats work. PDF preserves your layout.</li>
</ul>

<h2>Best ATS-Friendly CV Templates for 2026</h2>
<h3>Template 1: The Classic Reverse Chronological</h3>
<p>This is the most widely accepted format. It lists your work history from most recent to oldest. Best for: Corporate jobs and experienced professionals.</p>
<h3>Template 2: The Hybrid Combination</h3>
<p>This format combines a skills section at the top with chronological experience below. Best for: Career changers and skill-heavy roles.</p>
<h3>Template 3: The Simple One-Pager</h3>
<p>Designed for early-career professionals or recent graduates. Keeps everything on one page.</p>

<h2>What to Avoid in 2026</h2>
<ul>
  <li><strong>Two-column layouts</strong> – Confuses ATS parsing.</li>
  <li><strong>Graphics and icons</strong> – ATS cannot read them.</li>
  <li><strong>Unusual fonts</strong> – Your CV may render as gibberish.</li>
</ul>
    `,
    contentAfterCTA: `
<h2>FAQ</h2>
<div class="space-y-6 mb-12">
  <div>
    <h4 class="font-bold text-lg mb-2">What is the best file format for ATS?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">DOCX is the most universally accepted. PDF works for most systems but not all.</p>
  </div>
  <div>
    <h4 class="font-bold text-lg mb-2">Do fancy templates work with ATS?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">No. Fancy templates with columns and graphics will be rejected.</p>
  </div>
</div>
<h2>Conclusion</h2>
<p>ATS-friendly formatting is not optional. If your CV does not follow the rules, you will never be seen. Let Alvaru handle the formatting so you can focus on the job.</p>
    `
  },
  "what-are-ats-keywords": {
    title: "What Are ATS Keywords and How to Find Them",
    slug: "what-are-ats-keywords",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "7 min read",
    image: "/blog-ats-keywords.jpg",
    category: "Job Search Mastery",
    description: "Learn how to identify the most important keywords in a job description and integrate them into your CV to pass the ATS screen.",
    contentBeforeCTA: `
<p class="lead">You have the right experience. You have the right skills. But your CV is still getting rejected. The culprit is often missing keywords.</p>
<p>Applicant Tracking Systems (ATS) scan CVs for specific words and phrases from the job description. If those keywords are not in your CV, the system assumes you are not qualified.</p>
<p>This guide explains what ATS keywords are, how to find them, and how to use them effectively.</p>

<h2>What Are ATS Keywords?</h2>
<p>ATS keywords are specific words or phrases that match the job description. They include hard skills, soft skills, qualifications, and job-specific terms.</p>
<ul>
  <li><strong>Hard skills</strong> – Python, Salesforce, Google Ads, AWS.</li>
  <li><strong>Soft skills</strong> – Leadership, communication, problem-solving.</li>
  <li><strong>Qualifications</strong> – PMP certified, MBA, CPA.</li>
  <li><strong>Job-specific terms</strong> – Revenue forecasting, supply chain optimization.</li>
</ul>

<h2>How to Find ATS Keywords in Any Job Description</h2>
<h3>Method 1: Manual Extraction</h3>
<p>Copy the job description into a document. Highlight any word or phrase that appears more than once. Pay special attention to the "Requirements" and "Responsibilities" sections.</p>
<h3>Method 2: Use a Keyword Extractor</h3>
<p>Paste the job description into a free keyword extraction tool. The tool will identify the most frequent and relevant terms.</p>
<h3>Method 3: Look at Similar Job Postings</h3>
<p>Search for three to five similar roles at other companies. Compare their requirements. The overlapping keywords are critical for your industry.</p>

<h2>How to Add Keywords to Your CV Without Keyword Stuffing</h2>
<p>Keyword stuffing means adding keywords randomly without context. Do this instead:</p>
<ul>
  <li><strong>Integrate keywords into your bullet points</strong> – Show how you applied the skill.</li>
  <li><strong>Create a dedicated skills section</strong> – List 8-12 relevant hard skills at the top.</li>
  <li><strong>Use variations of the same keyword</strong> – Use "project management" and "managed projects".</li>
</ul>
    `,
    contentAfterCTA: `
<h2>Common ATS Keyword Mistakes</h2>
<ul>
  <li><strong>Using too few keywords</strong> – Aim for 10-15 per job.</li>
  <li><strong>Using irrelevant keywords</strong> – The recruiter will see the mismatch.</li>
  <li><strong>Ignoring job titles</strong> – Use the exact job title from the description if it fits.</li>
  <li><strong>Copying the entire job description</strong> – The ATS will flag it as spam.</li>
</ul>

<h2>FAQ</h2>
<div class="space-y-6 mb-12">
  <div>
    <h4 class="font-bold text-lg mb-2">How many keywords should I include?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">10-15 relevant keywords per job is a good target.</p>
  </div>
  <div>
    <h4 class="font-bold text-lg mb-2">Does the ATS scan for soft skills?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">Yes, but they are harder to detect. Include them in your professional summary.</p>
  </div>
</div>

<h2>Conclusion</h2>
<p>ATS keywords determine whether your CV is seen or ignored. You can extract them manually or let Alvaru do it automatically in seconds.</p>
    `
  },
  "how-to-auto-apply-on-linkedin": {
    title: "How to Auto-Apply on LinkedIn (Step by Step)",
    slug: "how-to-auto-apply-on-linkedin",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "8 min read",
    image: "/blog-linkedin-auto-apply.jpg",
    category: "Automation",
    description: "Save hours of repetitive work with our complete guide to using the Alvaru extension to auto-apply for jobs on LinkedIn.",
    contentBeforeCTA: `
<p class="lead">LinkedIn is the largest job search platform in the world. Applying manually takes time. Auto-apply tools change that.</p>
<p>This guide shows you how to auto-apply on LinkedIn using Alvaru. You will learn the exact steps to save hours every week.</p>

<h2>Why Auto-Apply on LinkedIn?</h2>
<ul>
  <li><strong>Speed</strong> – Manual applications take minutes; auto-apply takes seconds.</li>
  <li><strong>Consistency</strong> – Fill every form accurately without typos.</li>
  <li><strong>Focus</strong> – Spend time finding jobs, not filling forms.</li>
  <li><strong>Volume</strong> – Apply to more roles to increase your interview rate.</li>
</ul>

<h2>How Auto-Apply Works with Alvaru</h2>
<p>The Alvaru extension fills application forms automatically. It detects fields, uploads your CV, and answers common questions.</p>

<h2>Step by Step: Set Up Auto-Apply on LinkedIn</h2>
<h3>Step 1: Create Your Alvaru Account</h3>
<p>Sign up at alvaru.com and upload your base CV. This is the source for all tailored applications.</p>
<h3>Step 2: Install the Chrome Extension</h3>
<p>Add the Alvaru extension from the Chrome Web Store and pin it to your toolbar.</p>
<h3>Step 3: Connect to Alvaru</h3>
<p>Sign in via the extension to link it to your profile and data.</p>
<h3>Step 4: Click "Tailor & Apply" on LinkedIn</h3>
<p>When you find a job, click the Alvaru icon and select "Tailor & Apply". Alvaru will rewrite your CV for that specific role and fill the LinkedIn form instantly.</p>
    `,
    contentAfterCTA: `
<h2>Auto-Apply Settings You Should Know</h2>
<ul>
  <li><strong>Review First Mode</strong> – Alvaru fills the form but waits for your approval before submitting. Highly recommended.</li>
  <li><strong>Auto-Submit Mode</strong> – For maximum speed, Alvaru can submit forms automatically after filling.</li>
</ul>

<h2>FAQ</h2>
<div class="space-y-6 mb-12">
  <div>
    <h4 class="font-bold text-lg mb-2">Will LinkedIn ban me for using auto-apply?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">No. Alvaru fills forms just like a human would. It is safe and compliant with LinkedIn's terms.</p>
  </div>
  <div>
    <h4 class="font-bold text-lg mb-2">Can I still customize my application?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">Yes. Use "Review First Mode" to make any adjustments before the final submission.</p>
  </div>
</div>

<h2>Conclusion</h2>
<p>Auto-apply on LinkedIn saves hours of time. Set up Alvaru today and spend your time preparing for interviews instead of filling forms.</p>
    `
  },
  "how-to-write-a-cv-that-passes-ats": {
    title: "How to Write a CV That Passes ATS (2026 Guide)",
    slug: "how-to-write-a-cv-that-passes-ats",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "9 min read",
    image: "/blog-cv-pass-ats.jpg",
    category: "CV Writing",
    description: "The ultimate 10-rule checklist for writing a CV that clears Applicant Tracking System (ATS) filters and gets you seen by recruiters.",
    contentBeforeCTA: `
<p class="lead">You have the skills. You have the experience. But your CV is still hearing nothing back. The problem is the Applicant Tracking System (ATS).</p>
<p>ATS software scans your CV before a human sees it. If your CV is not formatted correctly, it gets rejected automatically. This guide shows you exactly how to write a CV that passes ATS filters.</p>

<h2>What Is an ATS and Why Does It Matter?</h2>
<p>An Applicant Tracking System is software used by companies to manage applications. It scans, sorts, and filters CVs based on keywords and formatting. Over 75% of large companies use them.</p>

<h2>The 10 Rules for an ATS-Friendly CV</h2>
<ul>
  <li><strong>Rule 1: Use a Single-Column Layout</strong> – Multiple columns confuse ATS parsers.</li>
  <li><strong>Rule 2: Choose Standard Fonts</strong> – Use Arial, Calibri, Helvetica, or Times New Roman.</li>
  <li><strong>Rule 3: Save as DOCX or PDF</strong> – DOCX is safest, but text-based PDF works well too.</li>
  <li><strong>Rule 4: Never Use Headers or Footers</strong> – Contact info in headers may be missed.</li>
  <li><strong>Rule 5: Avoid Tables and Text Boxes</strong> – These elements break the parsing logic.</li>
  <li><strong>Rule 6: Use Standard Section Headings</strong> – Use "Work Experience" and "Education".</li>
  <li><strong>Rule 7: Include Relevant Keywords</strong> – Match the words from the job description.</li>
  <li><strong>Rule 8: Integrate Keywords Naturally</strong> – Use them in context, not as a list.</li>
  <li><strong>Rule 9: Keep Formatting Simple</strong> – No graphics, icons, or progress bars.</li>
  <li><strong>Rule 10: Save with a Clear Filename</strong> – FirstName-LastName-CV.pdf</li>
</ul>

<h2>What to Avoid at All Costs</h2>
<ul>
  <li>Two-column layouts</li>
  <li>Graphics, icons, and progress bars</li>
  <li>Tables and text boxes</li>
  <li>Unusual or decorative fonts</li>
  <li>Misspelled keywords</li>
</ul>
    `,
    contentAfterCTA: `
<h2>How to Test If Your CV Is ATS-Friendly</h2>
<h3>Method 1: Use an ATS Checker Tool</h3>
<p>Upload your CV to a free checker to see formatting errors and missing keywords.</p>
<h3>Method 2: Copy and Paste into Notepad</h3>
<p>If the text remains in a logical order in plain text, the ATS will likely read it correctly.</p>

<h2>FAQ</h2>
<div class="space-y-6 mb-12">
  <div>
    <h4 class="font-bold text-lg mb-2">Can ATS read PDF files?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">Most modern systems can, but image-based PDFs are invisible to them.</p>
  </div>
  <div>
    <h4 class="font-bold text-lg mb-2">How long should my CV be?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">2 pages for experienced pros, 1 page for early career.</p>
  </div>
</div>

<h2>Conclusion</h2>
<p>ATS software is not going away. Follow these rules or let Alvaru handle the formatting for you. Optimization is the key to getting interviews.</p>
    `
  },
  "7-common-cv-mistakes": {
    title: "7 Common CV Mistakes That Get You Rejected by ATS",
    slug: "7-common-cv-mistakes",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "7 min read",
    image: "/blog-cv-mistakes.png",
    category: "Job Search Strategy",
    description: "Learn about the seven most frequent CV errors that trigger automatic Applicant Tracking System (ATS) rejection and how to fix them.",
    contentBeforeCTA: `
<p class="lead">You spend hours perfecting your CV. You hit submit. You wait. Nothing happens. The ATS rejected you—not because you are unqualified, but because your CV broke one of its rules.</p>
<p>This guide covers the 7 most common CV mistakes that trigger ATS rejection. Avoid these errors and start getting more interviews.</p>

<h2>Mistake 1: Using a Two-Column Layout</h2>
<p><strong>Why It Fails:</strong> ATS software scans top to bottom, left to right. Two-column layouts confuse the parser. Text gets jumbled. Sections get read out of order. Your contact information may end up in the middle of your experience section.</p>
<p><strong>The Fix:</strong> Use a single-column layout. Top to bottom. Left to right. No exceptions.</p>

<h2>Mistake 2: Putting Text in Headers or Footers</h2>
<p><strong>Why It Fails:</strong> Many ATS systems do not read headers or footers at all. They scan only the main body of the document. If your name or email is in a header, the ATS may not see it.</p>
<p><strong>The Fix:</strong> Put all contact information in the main body of the document at the top of the page.</p>

<h2>Mistake 3: Using Tables or Text Boxes</h2>
<p><strong>Why It Fails:</strong> ATS software cannot read text inside tables or text boxes. That information is completely invisible to the scanner. If your skills are in a table, they do not exist to the ATS.</p>
<p><strong>The Fix:</strong> Use simple bullet points. No tables. No text boxes. No columns.</p>

<h2>Mistake 4: Choosing Unusual Fonts</h2>
<p><strong>Why It Fails:</strong> ATS systems are designed to read standard fonts. Unusual fonts may not render correctly, turning your text into gibberish.</p>
<p><strong>The Fix:</strong> Use Arial, Calibri, Helvetica, or Times New Roman. These are safe and universally accepted.</p>
    `,
    contentAfterCTA: `
<h2>Mistake 5: Saving in the Wrong File Format</h2>
<p><strong>Why It Fails:</strong> Image-based PDFs contain no readable text. If the ATS cannot open or read your file, your application is rejected automatically.</p>
<p><strong>The Fix:</strong> Save as DOCX or a text-based PDF. DOCX is the most universally accepted format.</p>

<h2>Mistake 6: Keyword Stuffing</h2>
<p><strong>Why It Fails:</strong> Adding keywords randomly without context looks spammy. Recruiters who review CVs that pass the ATS will see this and reject you manually.</p>
<p><strong>The Fix:</strong> Integrate keywords naturally into your bullet points. Show how you used the skill with context and results.</p>

<h2>Mistake 7: Missing Keywords Entirely</h2>
<p><strong>Why It Fails:</strong> If your CV does not contain keywords from the job description, the ATS assumes you are not qualified. Your match score will be too low.</p>
<p><strong>The Fix:</strong> Read the job description carefully. Identify the key requirements and ensure those exact words appear in your CV in context.</p>

<h2>How to Know If Your CV Has These Mistakes</h2>
<div class="space-y-6 my-10">
  <div class="bg-slate-50 dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800">
    <h4 class="font-bold text-lg mb-2">Method 1: Paste into Notepad</h4>
    <p class="text-sm">Copy your CV and paste it into Notepad. If the text is garbled or out of order, the ATS will struggle to read it.</p>
  </div>
  <div class="bg-slate-50 dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800">
    <h4 class="font-bold text-lg mb-2">Method 2: Use an ATS Checker Tool</h4>
    <p class="text-sm">Upload your CV to a free ATS checker. It will flag formatting errors and missing keywords automatically.</p>
  </div>
  <div class="bg-slate-50 dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800">
    <h4 class="font-bold text-lg mb-2">Method 3: Use Alvaru</h4>
    <p class="text-sm">Alvaru generates ATS-friendly CVs automatically. The formatting is correct, and keywords are matched to each job description.</p>
  </div>
</div>

<h2>How Alvaru Helps You Avoid These Mistakes</h2>
<p>Alvaru is built to follow all ATS rules automatically. Every CV generated includes:</p>
<ul class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mb-10">
  <li>Single-column layout</li>
  <li>Standard safe fonts</li>
  <li>No headers or footers</li>
  <li>No tables or text boxes</li>
  <li>Natural keyword integration</li>
  <li>Full keyword matching</li>
</ul>

<h2>FAQ</h2>
<div class="space-y-6 mb-12">
  <div>
    <h4 class="font-bold text-lg mb-2">Can ATS read my CV if it has images?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">No. ATS cannot read images. Remove all graphics, icons, and photos to be safe.</p>
  </div>
  <div>
    <h4 class="font-bold text-lg mb-2">Does CV length affect ATS scoring?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">Not directly, but longer CVs have more space for keywords. Focus on relevance, not length.</p>
  </div>
  <div>
    <h4 class="font-bold text-lg mb-2">Is Alvaru guaranteed to pass ATS?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">No tool can guarantee, but Alvaru follows all best practices to significantly improve your chances.</p>
  </div>
</div>

<h2>Conclusion</h2>
<p>ATS rejection is frustrating, but most of it is preventable. Check your CV for these 7 mistakes and fix them, or use Alvaru to generate optimized CVs automatically. Stop getting rejected by bots.</p>
    `
  },
  "how-to-tailor-your-cv-for-a-career-change": {
    title: "How to Tailor Your CV for a Career Change (With Examples)",
    slug: "how-to-tailor-your-cv-for-a-career-change",
    date: "May 16, 2026",
    author: "Alvaru Team",
    readingTime: "8 min read",
    image: "/blog-career-change.png",
    category: "Career Change",
    description: "Struggling to switch industries? Learn how to reframe your experience, highlight transferable skills, and bypass ATS filters for your new career path.",
    contentBeforeCTA: `
<p class="lead">Changing careers is hard. You have the skills. You have the experience. But your CV is full of job titles and industries that do not match your new path.</p>
<p>Recruiters look at your CV and see someone from a different world. The ATS scans your CV and finds no matching keywords.</p>
<p>This guide shows you exactly how to tailor your CV for a career change. You will learn how to reframe your experience. You will see real examples. And you will know how to make recruiters see your potential, not your past.</p>

<h2>Why Career Change CVs Fail</h2>
<p>Most career change CVs fail for three reasons:</p>
<ol>
  <li><strong>Irrelevant job titles</strong> – Your previous titles do not match the role you want. A recruiter looking for a "Product Manager" sees "Marketing Manager" and moves on.</li>
  <li><strong>Missing keywords</strong> – Your CV lacks the specific terms from the new industry. The ATS gives you a low match score.</li>
  <li><strong>Wrong framing</strong> – You describe your experience in terms of your old industry. Recruiters cannot see how it applies to the new one.</li>
</ol>
<p>The good news is that these problems are fixable. You do not need to invent experience. You just need to reframe it.</p>

<h2>Step 1: Identify Transferable Skills</h2>
<p>Transferable skills are abilities that work across industries. They are your bridge. Common transferable skills include project management, data analysis, client communication, and team leadership.</p>

<h2>Step 2: Research Your Target Industry</h2>
<p>Find 5-10 job descriptions for roles you want. Identify the repeated keywords, required skills, and industry-specific terms. Write down the 15-20 most common keywords to include in your CV.</p>

<h2>Step 3: Rewrite Your Job Titles (Carefully)</h2>
<p>Do not lie, but you can reframe. Instead of "Marketing Manager," consider "Product Marketing Manager" if you managed product campaigns. The goal is to use titles that better reflect your actual duties while aligning with your target industry.</p>

<h2>Step 4: Reframe Your Bullet Points</h2>
<p>This is the most important step. Take your existing achievements and rewrite them to highlight transferable skills.</p>
<div class="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-xl my-8 font-body">
  <p class="font-bold mb-2 text-cyan-600">Example: Project Management</p>
  <p class="text-slate-500 mb-1 font-mono text-xs italic">Before: "Managed social media calendar for 5 clients"</p>
  <p class="text-slate-900 dark:text-white font-mono text-xs font-bold">After: "Managed cross-functional project timelines for 5 client accounts, ensuring on-time delivery"</p>
</div>

<h2>Step 5: Add a Career Summary Statement</h2>
<p>At the top of your CV, add a short summary that connects your past to your future. Use the template: <em>"Former [old role] transitioning into [new role]. Skilled in [transferable skill 1], [transferable skill 2], and [transferable skill 3]."</em></p>
    `,
    contentAfterCTA: `
<h2>Step 6: Create a Skills Section</h2>
<p>List 10-15 relevant skills at the top of your CV. Include both transferable skills and new industry terms you are learning. Examples: Product Strategy, Agile Methodology, Roadmap Planning.</p>

<h2>Step 7: Add a Projects or Certifications Section</h2>
<p>If you lack direct experience, show initiative. Include online courses (Coursera, Udemy), certifications (AWS, Scrum), or personal projects related to your new field.</p>

<h2>Full Example: Career Change CV</h2>
<div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-8 rounded-3xl my-12 shadow-inner font-body">
  <div class="border-b border-slate-100 dark:border-slate-700 pb-6 mb-6">
    <h3 class="text-2xl font-bold mb-2">Summary</h3>
    <p class="text-slate-600 dark:text-slate-400">Former Marketing Manager transitioning into Product Management. Skilled in market research, cross-functional leadership, and data-driven strategy. Seeking to apply 7 years of customer-focused experience to a Product Manager role in SaaS.</p>
  </div>
  
  <div class="border-b border-slate-100 dark:border-slate-700 pb-6 mb-6">
    <h3 class="text-2xl font-bold mb-4">Skills</h3>
    <div class="grid grid-cols-2 gap-2 text-sm">
      <li>Product Strategy</li>
      <li>User Research</li>
      <li>Agile Methodology</li>
      <li>Cross-Functional Leadership</li>
      <li>Data Analysis</li>
      <li>Roadmap Planning</li>
    </div>
  </div>

  <div>
    <h3 class="text-2xl font-bold mb-4">Work Experience</h3>
    <div class="mb-6">
      <p class="font-bold">Marketing Manager | TechStart Solutions | 2020-2025</p>
      <ul class="list-disc ml-5 mt-2 text-slate-600 dark:text-slate-400 space-y-1">
        <li>Led cross-functional teams to launch 5 digital products, managing timelines and stakeholder expectations</li>
        <li>Analyzed customer data and market trends to identify product opportunities, resulting in 2 new feature launches</li>
        <li>Collaborated with engineering and design teams to prioritize features based on user feedback and business goals</li>
      </ul>
    </div>
  </div>
</div>

<h2>FAQ</h2>
<div class="space-y-6 mb-12">
  <div>
    <h4 class="font-bold text-lg mb-2">Do I need to go back to school to change careers?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">Not always. Many career changes happen through transferable skills, certifications, and on-the-job learning.</p>
  </div>
  <div>
    <h4 class="font-bold text-lg mb-2">Should I remove unrelated experience?</h4>
    <p class="text-slate-600 dark:text-slate-400 font-body">Keep it but shorten it. Focus on transferable skills. Remove details that are completely irrelevant.</p>
  </div>
</div>

<h2>Conclusion</h2>
<p>Changing careers is challenging, but your CV does not have to hold you back. Focus on transferable skills, reframe your experience, and use the right keywords. Let Alvaru help you reframe your CV automatically.</p>
    `
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const blogPost = slug ? blogPosts[slug] : null;
  
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  if (!blogPost) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] dark:bg-[#0A0F1E]">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button variant="outline">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] dark:bg-[#0A0F1E] text-slate-900 dark:text-white transition-colors duration-300 font-body pb-20">
      <SeoHelmet
        title={`${blogPost.title} | Alvaru Blog`}
        description={blogPost.description}
        canonical={`https://www.alvaru.com/blog/${blogPost.slug}`}
      />
      <Navbar />

      <main className="pt-32">
        <article className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <Link to="/blog" className="inline-flex items-center text-sm font-bold text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors uppercase tracking-widest">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </motion.div>

          <header className="mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <span className="px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-[10px] font-bold uppercase tracking-widest mb-6 inline-block">
                {blogPost.category}
              </span>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-8 leading-[1.1]">
                {blogPost.title}
              </h1>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-wrap items-center justify-between gap-6 py-6 border-y border-slate-200 dark:border-slate-800"
            >
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center text-cyan-600 dark:text-cyan-400">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Author</p>
                    <p className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">{blogPost.author}</p>
                  </div>
                </div>
                <div className="h-8 w-px bg-slate-200 dark:bg-slate-800 hidden sm:block" />
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Published</p>
                    <p className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">{blogPost.date}</p>
                  </div>
                </div>
                <div className="h-8 w-px bg-slate-200 dark:bg-slate-800 hidden sm:block" />
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Reading Time</p>
                    <p className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">{blogPost.readingTime}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="rounded-full w-8 h-8 text-slate-400 hover:text-cyan-600 dark:hover:text-cyan-400" onClick={copyLink}>
                  <LinkIcon className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="rounded-full w-8 h-8 text-slate-400 hover:text-blue-600">
                  <Twitter className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="rounded-full w-8 h-8 text-slate-400 hover:text-blue-800">
                  <Linkedin className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          </header>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mb-16 rounded-[2.5rem] overflow-hidden aspect-[16/9] shadow-2xl relative"
          >
            <img
              src={blogPost.image}
              alt={blogPost.title}
              className="w-full h-full object-cover"
            />
          </motion.div>

          <div className="prose prose-lg prose-slate dark:prose-invert max-w-none 
              prose-headings:font-heading prose-headings:font-bold prose-headings:tracking-tight
              prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-8
              prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-4
              prose-p:text-slate-600 dark:prose-p:text-slate-400 prose-p:leading-relaxed prose-p:mb-6
              prose-li:text-slate-600 dark:prose-li:text-slate-400 prose-li:mb-2
              prose-strong:text-slate-900 dark:prose-strong:text-white
              prose-code:text-cyan-600 dark:prose-code:text-cyan-400 prose-code:bg-cyan-500/5 prose-code:px-1 prose-code:rounded
              prose-img:rounded-3xl
              [&_.lead]:text-xl [&_.lead]:md:text-2xl [&_.lead]:font-medium [&_.lead]:text-slate-900 dark:[&_.lead]:text-white [&_.lead]:mb-10
            ">
            <div dangerouslySetInnerHTML={{ __html: blogPost.contentBeforeCTA }} />
            
            <div className="my-16 p-8 md:p-12 rounded-[2rem] bg-gradient-to-br from-cyan-600 to-blue-700 text-white shadow-2xl relative overflow-hidden not-prose">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-5 h-5 text-cyan-300" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-200">The Modern Way</span>
                </div>
                <h2 className="text-2xl md:text-4xl font-heading font-bold mb-6 text-white leading-tight">How Alvaru Automates <br />CV Tailoring</h2>
                <p className="text-cyan-50 mb-10 leading-relaxed text-base md:text-lg max-w-2xl font-body">
                  Manual tailoring works, but it takes time. If you are applying to 20 jobs per week, that is 5 hours of rewriting. Alvaru automates the entire process in seconds.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10 font-body">
                  {[
                    "Upload your base CV once",
                    "Install the Chrome extension",
                    "Visit any job on LinkedIn",
                    "Click 'Tailor & Apply'"
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm font-semibold">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(103,232,249,0.5)]" />
                      {text}
                    </div>
                  ))}
                </div>
                
                <a href="https://alvaru.com" target="_blank" className="inline-block">
                  <Button className="bg-white hover:bg-cyan-50 text-cyan-700 font-bold rounded-xl px-10 py-6 h-auto shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]">
                    Try Alvaru for free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>

            <div dangerouslySetInnerHTML={{ __html: blogPost.contentAfterCTA }} />

            <div className="text-center mt-20 not-prose">
              <p className="font-bold text-2xl mb-8 font-heading">Start tailoring your CV with Alvaru today.</p>
              <Link to="/app">
                <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white dark:text-black font-bold rounded-xl px-12 py-7 h-auto shadow-2xl shadow-cyan-500/20 text-lg transition-all hover:scale-105">
                  Get Started Now
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-24 pt-12 border-t border-slate-200 dark:border-slate-800">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-4 text-left">
                <div className="w-16 h-16 rounded-full bg-cyan-500/10 flex items-center justify-center text-cyan-600 dark:text-cyan-400">
                  <User className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white mb-1">Written by {blogPost.author}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs leading-relaxed font-body">Career strategy experts dedicated to helping job seekers navigate the AI-driven recruitment world.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Link to="/blog">
                  <Button variant="outline" className="rounded-xl font-bold uppercase tracking-widest text-[10px] px-6 py-5 h-auto">All Posts</Button>
                </Link>
                <Link to="/app">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-white dark:text-black rounded-xl font-bold uppercase tracking-widest text-[10px] px-6 py-5 h-auto">Try Alvaru</Button>
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <section className="bg-slate-100 dark:bg-slate-900/30 mt-24 py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center text-left">
              <div>
                <h3 className="text-3xl md:text-4xl font-heading font-bold mb-6">Never miss an update.</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-10 text-lg font-body">Get the latest career advice, resume tips, and job market insights delivered straight to your inbox.</p>
                <div className="flex gap-3">
                  <input type="email" placeholder="you@example.com" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 flex-1 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all shadow-sm" />
                  <Button className="bg-slate-900 dark:bg-white text-white dark:text-black font-bold rounded-xl px-8 shadow-xl">Join</Button>
                </div>
              </div>
              <div className="relative group cursor-pointer" onClick={() => window.open('https://chromewebstore.google.com/detail/alvaru-smart-job-apply/laoahcalgdmfecdbiodloilhcafeoafl', '_blank')}>
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" />
                <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] p-10 flex items-center gap-6 shadow-xl">
                  <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center text-cyan-600 dark:text-cyan-400">
                    <Chrome className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl mb-1 font-heading">Install the Extension</h4>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] font-bold">Auto-apply on LinkedIn & Indeed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
